import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Award,
  FileCheck,
  ArrowRight,
  Loader2,
  CheckCircle,
  Circle,
  Clock,
  Globe
} from 'lucide-react';
import {
  getAnerkennungClusters,
  registerAnerkennung,
  getMyAnerkennung
} from '../services/api';
import { toast } from 'sonner';

const STEP_LABELS = {
  approbation: 'Approbation',
  fsp: 'Fachsprachprüfung',
  kenntnisprufung: 'Kenntnisprüfung',
  hospitation: 'Hospitation',
  gleichwertigkeit: 'Gleichwertigkeitsprüfung',
  anpassungsqualifizierung: 'Anpassungsqualifizierung',
  referenzprojekte: 'Referenzprojekte',
  meisterpruefung: 'Meisterprüfung'
};

const Anerkennung = () => {
  const [clusters, setClusters] = useState([]);
  const [myCase, setMyCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [formData, setFormData] = useState({
    cluster: '',
    current_step: '',
    documents_submitted: [],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clustersRes, caseRes] = await Promise.all([
        getAnerkennungClusters(),
        getMyAnerkennung().catch(() => ({ data: null }))
      ]);
      setClusters(clustersRes.data);
      setMyCase(caseRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!formData.cluster) {
      toast.error('Bitte wählen Sie eine Berufsgruppe');
      return;
    }
    setRegistering(true);
    try {
      const res = await registerAnerkennung(formData);
      setMyCase(res.data);
      setShowRegister(false);
      toast.success('Anerkennungsfall registriert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setRegistering(false);
    }
  };

  const selectedCluster = clusters.find(c => c.key === formData.cluster);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="anerkennung-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <Award className="w-8 h-8 text-primary" />
            Anerkennungs-Infrastruktur
          </h1>
          <p className="text-muted-foreground mt-1">
            Verfolgen Sie Ihren Anerkennungsprozess für ausländische Qualifikationen
          </p>
        </div>

        {myCase ? (
          <div className="space-y-6">
            {/* Progress Card */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    {myCase.cluster_name}
                  </span>
                  <Badge
                    className={myCase.status === 'abgeschlossen'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-blue-100 text-blue-700'
                    }
                  >
                    {myCase.status === 'abgeschlossen' ? 'Abgeschlossen' : 'In Bearbeitung'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Fortschritt</span>
                    <span className="text-sm font-bold text-primary">{myCase.progress_percent}%</span>
                  </div>
                  <Progress value={myCase.progress_percent} className="h-3" />
                </div>

                {/* Steps Timeline */}
                <div className="space-y-4">
                  <h4 className="font-medium">Anerkennungsschritte</h4>
                  <div className="space-y-3">
                    {myCase.steps?.map((step, idx) => {
                      const isComplete = idx < myCase.current_step_index;
                      const isCurrent = idx === myCase.current_step_index;
                      return (
                        <div
                          key={step}
                          className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                            isComplete
                              ? 'bg-green-50 border-green-200'
                              : isCurrent
                              ? 'bg-primary/5 border-primary'
                              : 'bg-muted/50 border-border'
                          }`}
                          data-testid={`step-${step}`}
                        >
                          {isComplete ? (
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                          ) : isCurrent ? (
                            <Clock className="w-6 h-6 text-primary flex-shrink-0" />
                          ) : (
                            <Circle className="w-6 h-6 text-muted-foreground flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className={`font-medium ${
                              isComplete ? 'text-green-700' :
                              isCurrent ? 'text-primary' : 'text-muted-foreground'
                            }`}>
                              Schritt {idx + 1}: {STEP_LABELS[step] || step}
                            </p>
                            {isCurrent && (
                              <p className="text-sm text-muted-foreground mt-1">Aktueller Schritt</p>
                            )}
                          </div>
                          {isComplete && (
                            <Badge variant="outline" className="border-green-200 text-green-700">Erledigt</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Documents */}
                {myCase.documents_submitted?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Eingereichte Dokumente</h4>
                    <div className="flex flex-wrap gap-2">
                      {myCase.documents_submitted.map((doc, idx) => (
                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                          <FileCheck className="w-3 h-3" />
                          {doc}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <div>
            {!showRegister ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Award className="w-16 h-16 mx-auto text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Anerkennung starten</h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-6">
                    Lassen Sie Ihre ausländische Qualifikation in Deutschland anerkennen.
                    Wir begleiten Sie durch den gesamten Prozess.
                  </p>
                  <Button
                    onClick={() => setShowRegister(true)}
                    className="rounded-full"
                    data-testid="start-anerkennung-btn"
                  >
                    Anerkennungsprozess starten
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Anerkennungsfall registrieren</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Berufsgruppe *</Label>
                    <Select
                      value={formData.cluster}
                      onValueChange={(v) => {
                        const cl = clusters.find(c => c.key === v);
                        setFormData({
                          ...formData,
                          cluster: v,
                          current_step: cl?.steps?.[0] || ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="cluster-select">
                        <SelectValue placeholder="Berufsgruppe wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clusters.map(c => (
                          <SelectItem key={c.key} value={c.key}>
                            {c.name} ({c.total_steps} Schritte)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedCluster && (
                    <div className="space-y-2">
                      <Label>Aktueller Schritt</Label>
                      <Select
                        value={formData.current_step}
                        onValueChange={(v) => setFormData({ ...formData, current_step: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedCluster.steps.map(step => (
                            <SelectItem key={step} value={step}>
                              {STEP_LABELS[step] || step}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Anmerkungen</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Zusätzliche Informationen zum Anerkennungsprozess..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowRegister(false)}
                      className="rounded-full"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleRegister}
                      disabled={registering || !formData.cluster}
                      className="rounded-full flex-1"
                      data-testid="submit-anerkennung-btn"
                    >
                      {registering && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Anerkennungsfall registrieren
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Anerkennung;
